import { genPoolId } from '../lib/proofGraph';
import { nonZeroNumber } from '../lib/format';
import { nonEmptyString } from '../lib/format';
import { errorMessage } from '../lib/errors';
import { DOC_CONFIG_DEFAULT, TASK_CONFIG_DEFAULT, ACTION_CONFIG_DEFAULT, CALENDAR_CONFIG_DEFAULT, COST_CONFIG_DEFAULT, type DocConfig, type TaskConfig, type ActionConfig, type CalendarConfig, type CostTargetConfig, type WidgetConfig } from '../lib/widgetConfig';
import { NotesWidgetConfig, NotesWidgetBody } from './stage-board/NotesWidget';
import { Select, Button } from './primitives';
import { Dialog } from './Dialog';
import { confirmAction } from '../lib/confirmation';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { GridLayout, useContainerWidth, noCompactor } from 'react-grid-layout';
import type { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { ModalDialog } from './ModalDialog';
import { sanitizeClioDocumentUrl } from '../types';
import {
  deleteWorkspaceTemplate,
  listWorkspaceTemplates,
  saveWorkspaceTemplate,
  setDefaultWorkspaceTemplate,
} from '../lib/workspaceTemplates';
import type {
  ClioTask, ClioDocument, WidgetPlacement, StrategicNote, CalendarEvent, ClioActivity,
  WorkspaceTemplate,
} from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────
const COLS = 6;
const MAX_ROWS = 6;
const ROW_HEIGHT = 140; // px per row unit
const DEFAULT_GRID_GAP = 12;
const DRAG_HANDLE = '.wg-drag-handle';

// ─── Widget Palette Definition ────────────────────────────────────────────────
interface WidgetDef {
  type: WidgetPlacement['type'];
  name: string;
  icon: string;
  defaultW: number;
  defaultH: number;
  description: string;
}

const WIDGET_DEFS: WidgetDef[] = [
  { type: 'action',          icon: '⚡', name: 'Quick-action Toolbar',       defaultW: 3, defaultH: 1, description: 'Stage-aware action buttons' },
  { type: 'document',        icon: '📂', name: 'Document Pipeline Tracker',  defaultW: 2, defaultH: 2, description: 'Track files through legal stages' },
  { type: 'task',            icon: '📋', name: 'Related Tasks',               defaultW: 2, defaultH: 2, description: 'Clio tasks scoped to this stage' },
  { type: 'cost',            icon: '💰', name: 'Cost & Budget Tracker',       defaultW: 2, defaultH: 1, description: 'Logged time vs billing budgets' },
  { type: 'calendar',        icon: '📅', name: 'Calendar Agenda / Grid',      defaultW: 2, defaultH: 2, description: 'Hearings, court rules, events' },
  { type: 'note',            icon: '📝', name: 'Strategic Notes',            defaultW: 2, defaultH: 2, description: 'Strategic notes for this matter' },
  { type: 'template_launch', icon: '📄', name: 'Template Creator',            defaultW: 2, defaultH: 1, description: 'One-click Clio document templates' },
];

// ─── Props ────────────────────────────────────────────────────────────────────
interface StageBoardProps {
  activeStageId: string;
  widgets: WidgetPlacement[];
  clioTasks: ClioTask[];
  clioDocuments: ClioDocument[];
  onOpenModal: (modalType: 'task' | 'calendar' | 'note' | 'time' | 'rules') => void;
  onToggleTaskStatus: (taskId: string) => void;
  onConfigureBoard: () => void;
  stageName: string;
  stageDesc: string;
  onUpdateWidgetConfig: (widgetId: string, newConfig: WidgetConfig) => void;
  onSaveLayout?: (stageId: string, updatedWidgets: WidgetPlacement[]) => void;
  hasSavedLayout?: boolean;
  matterId: string;
  strategicNotes: StrategicNote[];
  onUpdateStrategicNotes: (notes: StrategicNote[]) => void;
  calendarEvents: CalendarEvent[];
  onUpdateCalendarEvents: (events: CalendarEvent[]) => void;
  onAddCalendarEvent: (event: CalendarEvent) => void;
  clioActivities: ClioActivity[];
}


const CALENDAR_TYPE_DEFS = {
  hearing:    { label: 'Hearing',    color: '#ef4444', icon: '🏛️', bg: '#fee2e2' },
  meeting:    { label: 'Meeting',    color: '#3b82f6', icon: '🤝', bg: '#dbeafe' },
  deadline:   { label: 'Deadline',   color: '#f59e0b', icon: '⏰', bg: '#fef3c7' },
  deposition: { label: 'Deposition', color: '#8b5cf6', icon: '🎙️', bg: '#ede9fe' },
  trial:      { label: 'Trial',      color: '#10b981', icon: '⚖️', bg: '#d1fae5' },
};


// ─── Helpers: general ─────────────────────────────────────────────────────────

function getFileExt(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? (parts[parts.length - 1] ?? '').toLowerCase() : '';
}

function getFileTypeGroup(name: string): string {
  const ext = getFileExt(name);
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
  if (['jpg', 'jpeg', 'png', 'gif', 'tiff', 'svg'].includes(ext)) return 'image';
  if (['txt', 'csv', 'rtf', 'md'].includes(ext)) return 'text';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  return 'other';
}

const FILE_TYPE_OPTIONS = [
  { id: 'pdf',        label: 'PDF',        icon: '📕' },
  { id: 'word',       label: 'Word',       icon: '📘' },
  { id: 'excel',      label: 'Excel',      icon: '📗' },
  { id: 'powerpoint', label: 'PowerPoint', icon: '📙' },
  { id: 'image',      label: 'Images',     icon: '🖼️' },
  { id: 'text',       label: 'Text / CSV', icon: '📃' },
  { id: 'archive',    label: 'Archives',   icon: '🗜️' },
  { id: 'other',      label: 'Other',      icon: '📄' },
];

const EXT_ICON: Record<string, string> = {
  pdf: '📕', doc: '📘', docx: '📘', xls: '📗', xlsx: '📗',
  ppt: '📙', pptx: '📙', txt: '📃', csv: '📃', jpg: '🖼️',
  jpeg: '🖼️', png: '🖼️', gif: '🖼️', zip: '🗜️', rar: '🗜️',
};

function docIcon(name: string): string {
  return EXT_ICON[getFileExt(name)] ?? '📄';
}

function formatFileDate(iso?: string): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }); }
  catch { return iso.slice(0, 10); }
}

function formatFileSize(kb?: number): string {
  if (kb == null) return '';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// ─── Document Widget Defaults ────────────────────────────────────────────────



function applyDocFilters(docs: ClioDocument[], cfg: Partial<DocConfig>): ClioDocument[] {
  let out = [...docs];
  if (cfg.query?.trim()) {
    const q = cfg.query.trim().toLowerCase();
    out = out.filter((d) => d.name.toLowerCase().includes(q) || ((d.category ?? '')).toLowerCase().includes(q));
  }
  if (cfg.fileTypes && cfg.fileTypes.length > 0) {
    out = out.filter((d) => cfg.fileTypes!.includes(getFileTypeGroup(d.name)));
  }
  if (cfg.parentFolderId?.trim()) {
    out = out.filter((d) => d.parent?.id === cfg.parentFolderId);
  }
  if (cfg.createdAfter) {
    out = out.filter((d) => ((d.created_at ?? '')) >= cfg.createdAfter!);
  }
  if (cfg.createdBefore) {
    out = out.filter((d) => ((d.created_at ?? '')) <= cfg.createdBefore! + 'T23:59:59');
  }
  if (cfg.updatedAfter) {
    const field = (d: ClioDocument): string => ((nonEmptyString(d.updated_at) ?? d.created_at) ?? '');
    out = out.filter((d) => field(d) >= cfg.updatedAfter!);
  }
  if (cfg.updatedBefore) {
    const field = (d: ClioDocument): string => ((nonEmptyString(d.updated_at) ?? d.created_at) ?? '');
    out = out.filter((d) => field(d) <= cfg.updatedBefore! + 'T23:59:59');
  }
  if (cfg.createdBy?.trim()) {
    const cb = cfg.createdBy.trim().toLowerCase();
    out = out.filter((d) => ((d.created_by ?? '')).toLowerCase().includes(cb));
  }
  const sf = (nonEmptyString(cfg.sortField) ?? 'created_at');
  const sd = (nonEmptyString(cfg.sortDir) ?? 'desc');
  out.sort((a, b) => {
    let av = '', bv = '';
    if (sf === 'name')       { av = a.name; bv = b.name; }
    if (sf === 'created_at') { av = (a.created_at ?? ''); bv = (b.created_at ?? ''); }
    if (sf === 'updated_at') { av = ((nonEmptyString(a.updated_at) ?? a.created_at) ?? ''); bv = ((nonEmptyString(b.updated_at) ?? b.created_at) ?? ''); }
    return sd === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  return out;
}

function countActiveDocFilters(cfg: Partial<DocConfig>): number {
  let n = 0;
  if (cfg.query?.trim())          n++;
  if (cfg.fileTypes?.length)      n++;
  if (cfg.parentFolderId?.trim()) n++;
  if (cfg.createdAfter?.trim())   n++;
  if (cfg.createdBefore?.trim())  n++;
  if (cfg.updatedAfter?.trim())   n++;
  if (cfg.updatedBefore?.trim())  n++;
  if (cfg.createdBy?.trim())      n++;
  return n;
}

// ─── Task Widget Defaults ────────────────────────────────────────────────────



function applyTaskFilters(tasks: ClioTask[], cfg: Partial<TaskConfig>): ClioTask[] {
  let out = [...tasks];
  if (cfg.filterKeyword?.trim()) {
    const q = cfg.filterKeyword.trim().toLowerCase();
    out = out.filter((t) => t.name.toLowerCase().includes(q) || ((t.description ?? '')).toLowerCase().includes(q));
  }
  if (cfg.status && cfg.status !== 'all') {
    out = out.filter((t) => t.status === cfg.status);
  }
  if (cfg.createdBy?.trim()) {
    const cb = cfg.createdBy.trim().toLowerCase();
    out = out.filter((t) => ((t.created_by ?? '')).toLowerCase().includes(cb));
  }
  if (cfg.assignedTo?.trim()) {
    const ast = cfg.assignedTo.trim().toLowerCase();
    out = out.filter((t) => ((t.assignee ?? '')).toLowerCase().includes(ast));
  }
  if (cfg.dueAfter) {
    out = out.filter((t) => ((t.due_date ?? '')) >= cfg.dueAfter!);
  }
  if (cfg.dueBefore) {
    out = out.filter((t) => ((t.due_date ?? '')) <= cfg.dueBefore!);
  }
  const sf = (nonEmptyString(cfg.sortField) ?? 'due_date');
  const sd = (nonEmptyString(cfg.sortDir) ?? 'asc');
  out.sort((a, b) => {
    let av = '', bv = '';
    if (sf === 'name')     { av = a.name; bv = b.name; }
    if (sf === 'due_date') { av = (nonEmptyString(a.due_date) ?? '9999-12-31'); bv = (nonEmptyString(b.due_date) ?? '9999-12-31'); }
    if (sf === 'status')   { av = a.status; bv = b.status; }
    return sd === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  return out;
}

function countActiveTaskFilters(cfg: Partial<TaskConfig>): number {
  let n = 0;
  if (cfg.filterKeyword?.trim())          n++;
  if (cfg.status && cfg.status !== 'all') n++;
  if (cfg.createdBy?.trim())              n++;
  if (cfg.assignedTo?.trim())             n++;
  if (cfg.dueAfter?.trim())               n++;
  if (cfg.dueBefore?.trim())              n++;
  return n;
}

// ─── Action Toolbar Widget Defaults ──────────────────────────────────────────





// ─── Calendar Widget Defaults ────────────────────────────────────────────────



function applyCalendarFilters(events: CalendarEvent[], cfg: Partial<CalendarConfig>): CalendarEvent[] {
  let out = [...events];
  if (cfg.query?.trim()) {
    const q = cfg.query.trim().toLowerCase();
    out = out.filter((e) => e.title.toLowerCase().includes(q) || ((e.description ?? '')).toLowerCase().includes(q));
  }
  if (cfg.eventTypes && cfg.eventTypes.length > 0) {
    out = out.filter((e) => cfg.eventTypes!.includes(e.type));
  }
  if (cfg.createdBy?.trim()) {
    const cb = cfg.createdBy.trim().toLowerCase();
    out = out.filter((e) => e.creator.toLowerCase().includes(cb));
  }
  if (cfg.calendarName?.trim()) {
    const cal = cfg.calendarName.trim().toLowerCase();
    out = out.filter((e) => e.calendarName.toLowerCase().includes(cal));
  }
  if (cfg.dateAfter) {
    out = out.filter((e) => e.start.slice(0, 10) >= cfg.dateAfter!);
  }
  if (cfg.dateBefore) {
    out = out.filter((e) => e.start.slice(0, 10) <= cfg.dateBefore!);
  }
  out.sort((a, b) => a.start.localeCompare(b.start));
  return out;
}

function countActiveCalendarFilters(cfg: Partial<CalendarConfig>): number {
  let n = 0;
  if (cfg.query?.trim())        n++;
  if (cfg.eventTypes?.length)    n++;
  if (cfg.createdBy?.trim())    n++;
  if (cfg.calendarName?.trim()) n++;
  if (cfg.dateAfter?.trim())    n++;
  if (cfg.dateBefore?.trim())   n++;
  return n;
}

// ─── Cost & Budget Widget Defaults ───────────────────────────────────────────




type CostConfig = typeof COST_CONFIG_DEFAULT;

function sumMatchingActivities(activities: ClioActivity[], filter: {
  type?: 'TimeEntry' | 'ExpenseEntry' | 'all';
  categoryFilter?: string;
  userFilter?: string;
  dateAfter?: string;
  dateBefore?: string;
}): number {
  return activities
    .filter(act => {
      if (filter.type && filter.type !== 'all' && act.type !== filter.type) return false;
      if (filter.categoryFilter?.trim()) {
        const cat = filter.categoryFilter.trim().toLowerCase();
        const matchesCat = act.activity_description?.toLowerCase().includes(cat) || act.note?.toLowerCase().includes(cat);
        if (!matchesCat) return false;
      }
      if (filter.userFilter?.trim()) {
        const u = filter.userFilter.trim().toLowerCase();
        if (!act.user?.toLowerCase().includes(u)) return false;
      }
      if (filter.dateAfter?.trim()) {
        if (act.date < filter.dateAfter.trim()) return false;
      }
      if (filter.dateBefore?.trim()) {
        if (act.date > filter.dateBefore.trim()) return false;
      }
      return true;
    })
    .reduce((sum, act) => sum + act.total, 0);
}

// ─── DocWidgetConfig sub-component ───────────────────────────────────────────
interface DocWidgetConfigProps {
  widget: WidgetPlacement;
  clioDocuments: ClioDocument[];
  onSave: (cfg: DocConfig) => void;
  onCancel: () => void;
}

function DocWidgetConfig({ widget, clioDocuments, onSave, onCancel }: DocWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState<DocConfig>({ ...DOC_CONFIG_DEFAULT, ...(widget.config as Partial<DocConfig>) });
  const set = <K extends keyof DocConfig>(key: K, val: DocConfig[K]): void => setForm((p) => ({ ...p, [key]: val }));

  const availableFolders = useMemo((): [string, string][] => {
    const m = new Map<string, string>();
    for (const d of clioDocuments) {
      if (d.parent?.type === 'Folder') {
        m.set(d.parent.id, (nonEmptyString(d.parent.name) ?? `Folder ${d.parent.id}`));
      }
    }
    return Array.from(m.entries());
  }, [clioDocuments]);

  const availableAuthors = useMemo((): string[] => {
    const s = new Set<string>();
    for (const d of clioDocuments) { if (d.created_by) s.add(d.created_by); }
    return Array.from(s).sort();
  }, [clioDocuments]);

  const sectionHead = (label: string, badge?: string): React.JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '14px', marginBottom: '6px' }}>
      <span style={{ fontSize: '12px', fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</span>
      {badge && <span style={{ fontSize: '12px', background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: '4px', padding: '1px 5px', fontWeight: 700 }}>{badge}</span>}
    </div>
  );

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexShrink: 0 }}>
        <strong style={{ fontSize: '13px' }}>Configure Document Widget</strong>
        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Clio API aligned</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        {sectionHead('Display')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Page size</label>
            <Select value={form.pageSize} onChange={(e) => set('pageSize', Number(e.target.value) as 10 | 5 | 20 | 50)} >
              {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n} docs</option>)}
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Sort by</label>
            <Select value={form.sortField} onChange={(e) => set('sortField', e.target.value as "created_at" | "name" | "updated_at")} >
              <option value="created_at">Date Created</option>
              <option value="updated_at">Date Updated</option>
              <option value="name">File Name</option>
            </Select>
          </div>
        </div>
        <div style={{ marginTop: '6px' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Sort direction</label>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(['desc', 'asc'] as const).map((d) => (
              <button key={d} onClick={(): void => set('sortDir', d)}
                style={{ flex: 1, padding: '5px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  background: form.sortDir === d ? 'var(--primary)' : 'var(--page)',
                  color: form.sortDir === d ? 'white' : 'var(--muted)',
                  border: `1px solid ${form.sortDir === d ? 'var(--primary)' : 'var(--line)'}`,
                }}>
                {d === 'desc' ? '↓ Newest first' : '↑ Oldest first'}
              </button>
            ))}
          </div>
        </div>
        {sectionHead('Search', 'Clio: query')}
        <input type="text" value={form.query} placeholder="e.g. deposition..." onChange={(e) => set('query', e.target.value)} style={inputStyle} />
        {sectionHead('File Types')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
          {FILE_TYPE_OPTIONS.map((ft) => {
            const active = form.fileTypes.includes(ft.id);
            return (
              <button key={ft.id} onClick={(): void => set('fileTypes', active ? form.fileTypes.filter((x) => x !== ft.id) : [...form.fileTypes, ft.id])}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 400, background: active ? 'var(--primary-soft)' : 'var(--page)', color: active ? 'var(--primary)' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`, textAlign: 'left' }}>
                <span>{ft.icon}</span>{ft.label}
              </button>
            );
          })}
        </div>
        {sectionHead('Subfolder Filter', 'Clio: folder_id')}
        <Select value={form.parentFolderId} onChange={(e) => set('parentFolderId', e.target.value)} >
          <option value="">All folders</option>
          {availableFolders.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Select>
        {sectionHead('Created By')}
        <Select value={form.createdBy} onChange={(e) => set('createdBy', e.target.value)} >
          <option value="">All users</option>
          {availableAuthors.map((a) => <option key={a} value={a}>{a}</option>)}
        </Select>
        {sectionHead('Date Created', 'Clio: created_since/before')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <input type="date" value={form.createdAfter} onChange={(e) => set('createdAfter', e.target.value)} style={inputStyle} />
          <input type="date" value={form.createdBefore} onChange={(e) => set('createdBefore', e.target.value)} style={inputStyle} />
        </div>
        {sectionHead('Date Updated', 'Clio: updated_since/before')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <input type="date" value={form.updatedAfter} onChange={(e) => set('updatedAfter', e.target.value)} style={inputStyle} />
          <input type="date" value={form.updatedBefore} onChange={(e) => set('updatedBefore', e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid var(--line)', flexShrink: 0, marginTop: '8px' }}>
        <Button variant="ghost" type="submit" size="sm" onClick={(): void => setForm({ ...DOC_CONFIG_DEFAULT })} >Reset</Button>
        <div style={{ display: 'flex', gap: '6px' }}>
          <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
          <Button variant="primary" type="submit"  onClick={(): void => onSave(form)}>Apply</Button>
        </div>
      </div>
    </div>
  );
}

// ─── DocWidgetBody sub-component ─────────────────────────────────────────────
interface DocWidgetBodyProps {
  widget: WidgetPlacement;
  clioDocuments: ClioDocument[];
  page: number;
  onLoadMore: () => void;
}

function DocWidgetBody({
  widget, clioDocuments, page, onLoadMore,
}: DocWidgetBodyProps): React.JSX.Element {
  const cfg = useMemo(() => (widget.config as Partial<DocConfig> | undefined) ?? {}, [widget.config]);
  const pageSize = nonZeroNumber(cfg.pageSize) ?? 10;
  const activeFilters = countActiveDocFilters(cfg);

  const filtered = useMemo((): ClioDocument[] =>
    applyDocFilters(clioDocuments, cfg),
    [clioDocuments, cfg]
  );

  const clioPagedDocs: ClioDocument[]  = filtered.slice(0, page * pageSize);
  const hasMore       = clioPagedDocs.length < filtered.length;
  const totalAll      = clioDocuments.length;
  const totalShown    = clioPagedDocs.length;
  const totalFiltered = filtered.length;

  const chips: string[] = [];
  if (cfg.query?.trim())          chips.push(`"${cfg.query}"`);
  if (cfg.fileTypes?.length)      chips.push(cfg.fileTypes.map((t) => FILE_TYPE_OPTIONS.find((o) => o.id === t)?.label ?? t).join(', '));
  if (cfg.parentFolderId?.trim()) chips.push(`Folder: ${cfg.parentFolderId}`);
  if (cfg.createdBy?.trim())      chips.push(`By: ${cfg.createdBy}`);
  if (cfg.createdAfter?.trim() || cfg.createdBefore?.trim()) chips.push('Date range active');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Subtitle heading removed completely as requested */}

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px', flexShrink: 0 }}>
          {chips.map((chip, i) => (
            <span key={i} style={{ fontSize: '12px', background: 'var(--page)', border: '1px solid var(--line)', borderRadius: '8px', padding: '2px 7px', color: 'var(--ink)' }}>
              {chip}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', flexShrink: 0 }}>
        <span>
          Showing <b style={{ color: 'var(--ink)' }}>{totalShown}</b> of{' '}
          {activeFilters > 0
            ? <><b style={{ color: 'var(--primary)' }}>{totalFiltered}</b> filtered<span style={{ marginLeft: '2px' }}> ({totalAll} total)</span></>
            : <b style={{ color: 'var(--ink)' }}>{totalAll}</b>
          }
        </span>
        <span style={{ fontStyle: 'italic' }}>
          {(cfg.sortField === 'name' ? 'A–Z' : cfg.sortField === 'updated_at' ? 'Updated' : 'Created')}{' '}
          {cfg.sortDir === 'asc' ? '↑' : '↓'}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {filtered.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '8px', color: 'var(--muted)', padding: '20px 0' }}>
            <span style={{ fontSize: '24px' }}>🔍</span>
            <span style={{ fontSize: '12px', textAlign: 'center' }}>
              No documents match your filters.<br />
              <span style={{ fontSize: '12px' }}>Try adjusting your search or date range.</span>
            </span>
          </div>
        )}

        {clioPagedDocs.map((doc) => (
          <div key={doc.id} className="doc-row">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0, lineHeight: 1.2, marginTop: '1px' }}>
                {docIcon(doc.name)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a
                  href={sanitizeClioDocumentUrl((doc.deeplink ?? ''), doc.id)}
                  target="_blank" rel="noreferrer"
                  style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={doc.name}
                >
                  {doc.name}
                </a>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                  {doc.category && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{doc.category}</span>
                  )}
                  {doc.parent?.name && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      📁 {doc.parent.name}
                    </span>
                  )}
                  {doc.created_by && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      👤 {doc.created_by}
                    </span>
                  )}
                  {doc.size_kb != null && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      {formatFileSize(doc.size_kb)}
                    </span>
                  )}
                </div>
                {widget.h >= 2 && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '3px', fontSize: '12px', color: 'var(--muted)' }}>
                    <span title="Created">📅 {formatFileDate(doc.created_at)}</span>
                    {doc.updated_at && doc.updated_at !== doc.created_at && (
                      <span title="Updated">✏️ {formatFileDate(doc.updated_at)}</span>
                    )}
                  </div>
                )}
              </div>
              <a
                href={sanitizeClioDocumentUrl((doc.deeplink ?? ''), doc.id)}
                target="_blank" rel="noreferrer"
                style={{ fontSize: '12px', color: 'var(--primary)', textDecoration: 'none', flexShrink: 0, alignSelf: 'center', opacity: 0.7 }}
              >
                ↗
              </a>
            </div>
          </div>
        ))}
      </div>

      <div style={{ flexShrink: 0, marginTop: '8px', borderTop: '1px solid var(--line-soft)', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {totalShown} / {totalFiltered} shown
        </span>
        {hasMore ? (
          <button
            onClick={onLoadMore}
            style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-soft)', border: '1px solid var(--primary)', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer' }}
          >
            Load {Math.min(pageSize, totalFiltered - totalShown)} more ↓
          </button>
        ) : totalFiltered > 0 ? (
          <span style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>All loaded</span>
        ) : null}
      </div>
    </div>
  );
}

// ─── TaskWidgetConfig sub-component ──────────────────────────────────────────
interface TaskWidgetConfigProps {
  widget: WidgetPlacement;
  clioTasks: ClioTask[];
  onSave: (cfg: TaskConfig) => void;
  onCancel: () => void;
}

function TaskWidgetConfig({ widget, clioTasks, onSave, onCancel }: TaskWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState<TaskConfig>({ ...TASK_CONFIG_DEFAULT, ...(widget.config as Partial<TaskConfig>) });
  const set = <K extends keyof TaskConfig>(key: K, val: TaskConfig[K]): void => setForm((p) => ({ ...p, [key]: val }));

  const availableCreators = useMemo((): string[] => {
    const s = new Set<string>();
    for (const t of clioTasks) { if (t.created_by) s.add(t.created_by); }
    return Array.from(s).sort();
  }, [clioTasks]);

  const availableAssignees = useMemo((): string[] => {
    const s = new Set<string>();
    for (const t of clioTasks) { if (t.assignee) s.add(t.assignee); }
    return Array.from(s).sort();
  }, [clioTasks]);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px' }}>
      <strong style={{ fontSize: '13px', marginBottom: '8px' }}>Configure Tasks Widget</strong>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Keyword / Tag Search</label>
        <input type="text" value={form.filterKeyword} placeholder="Search tasks..." onChange={(e) => set('filterKeyword', e.target.value)} style={inputStyle} />

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Status</label>
        <Select value={form.status} onChange={(e) => set('status', e.target.value as "all" | "pending" | "in_progress" | "completed")} >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Created By</label>
        <Select value={form.createdBy} onChange={(e) => set('createdBy', e.target.value)} >
          <option value="">All creators</option>
          {availableCreators.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Assigned To</label>
        <Select value={form.assignedTo} onChange={(e) => set('assignedTo', e.target.value)} >
          <option value="">All assignees</option>
          {availableAssignees.map((a) => <option key={a} value={a}>{a}</option>)}
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Due Date Range</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <input type="date" value={form.dueAfter} onChange={(e) => set('dueAfter', e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
          <input type="date" value={form.dueBefore} onChange={(e) => set('dueBefore', e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
        </div>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Sort By</label>
        <Select value={form.sortField} onChange={(e) => set('sortField', e.target.value as "name" | "due_date" | "status")} >
          <option value="due_date">Due Date</option>
          <option value="name">Task Name</option>
          <option value="status">Status</option>
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Sort Direction</label>
        <Select value={form.sortDir} onChange={(e) => set('sortDir', e.target.value as "desc" | "asc")} >
          <option value="asc">Ascending (A-Z / Oldest first)</option>
          <option value="desc">Descending (Z-A / Newest first)</option>
        </Select>
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid var(--line)', marginTop: '8px' }}>
        <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit"  onClick={(): void => onSave(form)}>Save</Button>
      </div>
    </div>
  );
}

// ─── TaskWidgetBody sub-component ────────────────────────────────────────────
interface TaskWidgetBodyProps {
  widget: WidgetPlacement;
  clioTasks: ClioTask[];
  onToggleTaskStatus: (taskId: string) => void;
  activeStageId: string;
}

function TaskWidgetBody({ widget, clioTasks, onToggleTaskStatus, activeStageId }: TaskWidgetBodyProps): React.JSX.Element {
  const cfg = useMemo(() => (widget.config as Partial<TaskConfig> | undefined) ?? {}, [widget.config]);
  const activeFilters = countActiveTaskFilters(cfg);

  const tasks = useMemo((): ClioTask[] => {
    if (Object.keys(cfg).length === 0) {
      return clioTasks.filter((t) => {
        const text = `${t.name} ${(t.description ?? '')}`.toLowerCase();
        const sk = activeStageId.toLowerCase();
        if (sk === 'presuit')   return text.includes('client') || text.includes('review') || text.includes('demand') || text.includes('investigation');
        if (sk === 'pleadings') return text.includes('complaint') || text.includes('answer') || text.includes('pleading') || text.includes('service') || text.includes('summons');
        if (sk === 'discovery') return text.includes('meet') || text.includes('confer') || text.includes('depo') || text.includes('written') || text.includes('discovery') || text.includes('interrogator') || text.includes('rfp');
        return true;
      });
    }
    return applyTaskFilters(clioTasks, cfg);
  }, [clioTasks, cfg, activeStageId]);

  const isOverdue = (dueDate?: string): boolean => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const getStatusBadgeStyle = (status: string): { background: string; color: string; } => {
    if (status === 'completed') return { background: 'var(--t4s)', color: 'var(--t4)' };
    if (status === 'in_progress') return { background: 'var(--t1s)', color: 'var(--t1)' };
    return { background: 'var(--line)', color: 'var(--muted)' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Subtitle heading removed completely as requested */}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {tasks.length} task{tasks.length !== 1 ? 's' : ''} listed
        </span>
        {activeFilters > 0 && (
          <span style={{ fontSize: '12px', background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: '10px', padding: '2px 7px', fontWeight: 700 }}>
            {activeFilters} filter{activeFilters > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {tasks.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center', padding: '24px 0' }}>
            No tasks found matching current filters.
          </div>
        ) : (
          tasks.map((task) => (
            <label key={task.id} style={{ display: 'flex', gap: '8px', padding: '8px', background: 'var(--page)', borderRadius: '6px', border: '1px solid var(--line)', cursor: 'pointer', fontSize: '12.5px' }}>
              <input type="checkbox" checked={task.status === 'completed'} onChange={(): void => onToggleTaskStatus(task.id)} style={{ marginTop: '2px' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block', textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'var(--muted)' : 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.name}
                </strong>
                {widget.h >= 2 && task.description && (
                  <span style={{ display: 'block', fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{task.description}</span>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '4px' }}>
                  <span style={{ fontSize: '12px', padding: '1px 5px', borderRadius: '4px', textTransform: 'capitalize', ...getStatusBadgeStyle(task.status) }}>
                    {task.status.replace('_', ' ')}
                  </span>
                  {task.due_date && (
                    <span style={{ fontSize: '12px', color: isOverdue(task.due_date) ? 'var(--t0)' : 'var(--muted)', fontWeight: isOverdue(task.due_date) ? 'bold' : 'normal' }}>
                      📅 Due: {task.due_date} {isOverdue(task.due_date) && '(Overdue)'}
                    </span>
                  )}
                  {task.assignee && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      👤 Assignee: {task.assignee}
                    </span>
                  )}
                </div>
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

// ─── ActionWidgetConfig sub-component ────────────────────────────────────────
interface ActionWidgetConfigProps {
  widget: WidgetPlacement;
  onSave: (cfg: ActionConfig) => void;
  onCancel: () => void;
}

function ActionWidgetConfig({ widget, onSave, onCancel }: ActionWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState<ActionConfig>({
    enabledActions: ACTION_CONFIG_DEFAULT.enabledActions,
    customLinks: ACTION_CONFIG_DEFAULT.customLinks,
    ...widget.config,
  });

  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkIcon, setNewLinkIcon] = useState('🔗');

  const addCustomLink = (): void => {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) return;
    setForm((prev) => ({
      ...prev,
      customLinks: [...prev.customLinks, { label: newLinkLabel.trim(), url: newLinkUrl.trim(), icon: newLinkIcon }],
    }));
    setNewLinkLabel('');
    setNewLinkUrl('');
    setNewLinkIcon('🔗');
  };

  const removeCustomLink = (idx: number): void => {
    setForm((prev) => ({
      ...prev,
      customLinks: prev.customLinks.filter((_, i) => i !== idx),
    }));
  };

  const toggleAction = (actionId: string): void => {
    setForm((prev) => {
      const active = prev.enabledActions.includes(actionId);
      return {
        ...prev,
        enabledActions: active
          ? prev.enabledActions.filter((x) => x !== actionId)
          : [...prev.enabledActions, actionId],
      };
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  const actionOptions = [
    { id: 'rules',       name: '⚖️ Rules Docket' },
    { id: 'time',        name: '⏱️ Log Billable Time' },
    { id: 'calendar',    name: '📅 Schedule Event' },
    { id: 'note',        name: '📝 Log Case Note' },
    { id: 'mapper',      name: '⚖️ Proof Graph Tab' },
    { id: 'negotiation', name: '🤝 Negotiation Tab' },
    { id: 'parties',     name: '👥 Parties Tab' },
    { id: 'visualizer',  name: '📊 Graph Visualizer Tab' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px' }}>
      <strong style={{ fontSize: '13px', marginBottom: '8px' }}>Configure Action Toolbar</strong>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        <span style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase' }}>Enabled Actions</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '14px' }}>
          {actionOptions.map((opt) => {
            const active = form.enabledActions.includes(opt.id);
            return (
              <button key={opt.id} onClick={(): void => toggleAction(opt.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 400, background: active ? 'var(--primary-soft)' : 'var(--page)', color: active ? 'var(--primary)' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`, textAlign: 'left' }}>
                {opt.name}
              </button>
            );
          })}
        </div>

        <span style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase' }}>Custom Links</span>
        {form.customLinks.map((link, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: 'var(--page)', borderRadius: '6px', border: '1px solid var(--line)', marginBottom: '6px' }}>
            <span>{(nonEmptyString(link.icon) ?? '🔗')}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 'bold' }}>{link.label}</div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{link.url}</div>
            </div>
            <Button variant="ghost" type="submit" onClick={(): void => removeCustomLink(idx)} >×</Button>
          </div>
        ))}

        <div style={{ border: '1px dashed var(--line)', padding: '10px', borderRadius: '8px', marginTop: '10px', background: '#fafafa' }}>
          <span style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Add Custom Link Button</span>
          <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: '6px', marginBottom: '6px' }}>
            <input type="text" value={newLinkIcon} onChange={(e) => setNewLinkIcon(e.target.value)} style={{ ...inputStyle, marginBottom: 0, textAlign: 'center' }} placeholder="Icon" />
            <input type="text" value={newLinkLabel} onChange={(e) => setNewLinkLabel(e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} placeholder="Button Label (e.g. Matter Folder)" />
          </div>
          <input type="text" value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} style={inputStyle} placeholder="URL Template (use {{matter_id}} for Matter ID)" />
          <button onClick={addCustomLink} style={{ background: 'var(--primary-soft)', color: 'var(--primary)', border: '1px solid var(--primary)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}>Add custom button</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid var(--line)', marginTop: '8px' }}>
        <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit"  onClick={(): void => onSave(form)}>Save</Button>
      </div>
    </div>
  );
}

// ─── ActionWidgetBody sub-component ──────────────────────────────────────────
interface ActionWidgetBodyProps {
  widget: WidgetPlacement;
  onOpenModal: (modalType: 'task' | 'calendar' | 'note' | 'time' | 'rules') => void;
  matterId: string;
}

function ActionWidgetBody({ widget, onOpenModal, matterId }: ActionWidgetBodyProps): React.JSX.Element {
  const cfg = useMemo<Partial<ActionConfig>>(() => widget.config ?? {}, [widget.config]);
  const enabled = cfg.enabledActions ?? ACTION_CONFIG_DEFAULT.enabledActions;
  const customLinks = cfg.customLinks ?? ACTION_CONFIG_DEFAULT.customLinks;

  const navigateCustomLink = (urlTemplate: string): void => {
    const finalUrl = urlTemplate.replace(/\{\{\s*matter_id\s*\}\}/g, matterId);
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const clickNavTab = (tabName: string): void => {
    const navItems = document.querySelectorAll('.nav .navitem');
    for (let i = 0; i < navItems.length; i++) {
      const el = navItems[i] as HTMLElement;
      if (el.textContent?.toLowerCase().includes(tabName)) {
        el.click();
        return;
      }
    }
  };

  const actionButtons = [
    { id: 'rules',       icon: '⚖️', label: 'Rules Docket', action: (): void => onOpenModal('rules') },
    { id: 'time',        icon: '⏱️', label: 'Log Time',     action: (): void => onOpenModal('time') },
    { id: 'calendar',    icon: '📅', label: 'Schedule Event', action: (): void => onOpenModal('calendar') },
    { id: 'note',        icon: '📝', label: 'Log Note',     action: (): void => onOpenModal('note') },
    { id: 'mapper',      icon: '⚖️', label: 'Proof Graph',  action: (): void => clickNavTab('proof graph') },
    { id: 'negotiation', icon: '🤝', label: 'Negotiation',  action: (): void => clickNavTab('negotiation') },
    { id: 'parties',     icon: '👥', label: 'Parties Tab',   action: (): void => clickNavTab('parties') },
    { id: 'visualizer',  icon: '📊', label: 'Visualizer',   action: (): void => clickNavTab('visualizer') },
  ];

  const visibleButtons = actionButtons.filter((btn) => enabled.includes(btn.id));

  const totalButtons = visibleButtons.length + customLinks.length;
  const showText = widget.w >= 3 && totalButtons <= 4;

  if (widget.h === 1) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', minHeight: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', width: '100%', height: '100%', alignItems: 'center', minWidth: 0 }}>
          {visibleButtons.map((btn) => (
            <button
              key={btn.id}
              className="qa"
              onClick={btn.action}
              title={btn.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: showText ? '6px' : '0',
                padding: showText ? '8px 10px' : '8px',
                height: '100%',
                maxHeight: '44px',
                flex: 1,
                minWidth: 0,
                border: '1px solid var(--line)',
                borderRadius: '8px',
                background: 'var(--page)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{btn.icon}</span>
              {showText && (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {btn.label}
                </span>
              )}
            </button>
          ))}
          {customLinks.map((link, idx) => (
            <button
              key={`custom_${idx}`}
              className="qa"
              onClick={(): void => navigateCustomLink(link.url)}
              title={link.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: showText ? '6px' : '0',
                padding: showText ? '8px 10px' : '8px',
                height: '100%',
                maxHeight: '44px',
                flex: 1,
                minWidth: 0,
                border: '1px solid var(--line)',
                borderRadius: '8px',
                background: 'var(--page)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{(nonEmptyString(link.icon) ?? '🔗')}</span>
              {showText && (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {link.label}
                </span>
              )}
            </button>
          ))}
          {totalButtons === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center', width: '100%' }}>
              No actions configured. Click ⚙.
            </div>
          )}
        </div>
      </div>
    );
  }

  let gridCols = 'repeat(auto-fill, minmax(130px, 1fr))';
  if (widget.w <= 1) {
    gridCols = '1fr';
  } else if (widget.w <= 2) {
    gridCols = '1fr 1fr';
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', flex: 1, overflowY: 'auto' }}>
        {visibleButtons.map((btn) => (
          <button key={btn.id} className="qa" onClick={btn.action} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '8px', background: 'var(--page)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{btn.icon}</span>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <b style={{ display: 'block', fontSize: '12px', color: 'var(--ink)', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{btn.label}</b>
            </div>
          </button>
        ))}

        {customLinks.map((link, idx) => (
          <button key={`custom_${idx}`} className="qa" onClick={(): void => navigateCustomLink(link.url)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: '8px', background: 'var(--page)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{(nonEmptyString(link.icon) ?? '🔗')}</span>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <b style={{ display: 'block', fontSize: '12px', color: 'var(--ink)', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{link.label}</b>
            </div>
          </button>
        ))}

        {visibleButtons.length === 0 && customLinks.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            All actions disabled. Click ⚙ to configure.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CalendarWidgetConfig sub-component ──────────────────────────────────────
interface CalendarWidgetConfigProps {
  widget: WidgetPlacement;
  allEvents: CalendarEvent[];
  onSave: (cfg: CalendarConfig) => void;
  onCancel: () => void;
}

function CalendarWidgetConfig({ widget, allEvents, onSave, onCancel }: CalendarWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState<CalendarConfig>({ ...CALENDAR_CONFIG_DEFAULT, ...widget.config });
  const set = <K extends keyof CalendarConfig>(key: K, val: CalendarConfig[K]): void => setForm((p) => ({ ...p, [key]: val }));

  const availableCreators = useMemo((): string[] => {
    const s = new Set<string>();
    for (const e of allEvents) { if (e.creator) s.add(e.creator); }
    return Array.from(s).sort();
  }, [allEvents]);

  const availableCalendars = useMemo((): string[] => {
    const s = new Set<string>();
    for (const e of allEvents) { if (e.calendarName) s.add(e.calendarName); }
    return Array.from(s).sort();
  }, [allEvents]);

  const toggleEventType = (id: string): void => {
    set('eventTypes', form.eventTypes.includes(id)
      ? form.eventTypes.filter((x) => x !== id)
      : [...form.eventTypes, id]);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px' }}>
      <strong style={{ fontSize: '13px', marginBottom: '8px' }}>Configure Calendar Widget</strong>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Subject Keyword Search</label>
        <input type="text" value={form.query} placeholder="Search events..." onChange={(e) => set('query', e.target.value)} style={inputStyle} />

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Event Types</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px' }}>
          {Object.entries(CALENDAR_TYPE_DEFS).map(([id, def]) => {
            const active = form.eventTypes.includes(id);
            return (
              <button key={id} onClick={(): void => toggleEventType(id)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 400, background: active ? 'var(--primary-soft)' : 'var(--page)', color: active ? 'var(--primary)' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`, textAlign: 'left' }}>
                <span>{def.icon}</span>{def.label}
              </button>
            );
          })}
        </div>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Created By</label>
        <Select value={form.createdBy} onChange={(e) => set('createdBy', e.target.value)} >
          <option value="">All creators</option>
          {availableCreators.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Calendar Name</label>
        <Select value={form.calendarName} onChange={(e) => set('calendarName', e.target.value)} >
          <option value="">All calendars</option>
          {availableCalendars.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Date Range</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <input type="date" value={form.dateAfter} onChange={(e) => set('dateAfter', e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
          <input type="date" value={form.dateBefore} onChange={(e) => set('dateBefore', e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid var(--line)', marginTop: '8px' }}>
        <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit"  onClick={(): void => onSave(form)}>Save</Button>
      </div>
    </div>
  );
}

// ─── CalendarEventEditorModal sub-component ──────────────────────────────────
interface CalendarEventEditorModalProps {
  onSave: (event: Omit<CalendarEvent, 'id'>) => void;
  onClose: () => void;
}

function CalendarEventEditorModal({ onSave, onClose }: CalendarEventEditorModalProps): React.JSX.Element {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('2026-07-12T09:00');
  const [end, setEnd] = useState('2026-07-12T10:00');
  const [type, setType] = useState<CalendarEvent['type']>('hearing');
  const [creator, setCreator] = useState('Nicholas Matlach');
  const [calendarName, setCalendarName] = useState('Litigation Calendar');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px'
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title,
      start,
      end,
      type,
      creator,
      calendarName,
      location,
      description,
    });
  };

  return (
    <Dialog label="Stage details" onClose={onClose} pending={false}>
      <form className="modal" onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <h3 style={{ borderBottom: '1px solid var(--line)', paddingBottom: '8px', margin: 0 }}>
          📅 Create Calendar Event
        </h3>

        <div>
          <label style={labelStyle}>Event Title / Summary</label>
          <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} placeholder="e.g. Deposition of John Doe" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <label style={labelStyle}>Start Date &amp; Time</label>
            <input type="datetime-local" required value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>End Date &amp; Time</label>
            <input type="datetime-local" required value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <label style={labelStyle}>Event Type</label>
            <Select value={type} onChange={(e) => setType(e.target.value as React.SetStateAction<"hearing" | "meeting" | "deadline" | "deposition" | "trial">)} >
              <option value="hearing">🏛️ Hearing</option>
              <option value="deposition">🎙️ Deposition</option>
              <option value="deadline">⏰ Deadline</option>
              <option value="trial">⚖️ Trial</option>
              <option value="meeting">🤝 Meeting</option>
            </Select>
          </div>
          <div>
            <label style={labelStyle}>Calendar Name</label>
            <Select value={calendarName} onChange={(e) => setCalendarName(e.target.value)} >
              <option value="Litigation Calendar">Litigation Calendar</option>
              <option value="Court Calendar">Court Calendar</option>
              <option value="Court Deadlines">Court Deadlines</option>
              <option value="Firm Internal">Firm Internal</option>
            </Select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <label style={labelStyle}>Creator</label>
            <input type="text" required value={creator} onChange={(e) => setCreator(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Location / Link</label>
            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} style={inputStyle} placeholder="e.g. Courtroom 4D / Zoom" />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '60px' }} placeholder="Event details..." />
        </div>

        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '6px' }}>
          <Button variant="secondary" type="button"  onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" >Save Event</Button>
        </div>
      </form>
    </Dialog>
  );
}

// ─── CalendarWidgetBody sub-component ────────────────────────────────────────
interface CalendarWidgetBodyProps {
  widget: WidgetPlacement;
  calendarEvents: CalendarEvent[];
  onUpdateCalendarEvents: (events: CalendarEvent[]) => void;
  onOpenCalendarCreate: () => void;
}

function CalendarWidgetBody({
  widget, calendarEvents, onUpdateCalendarEvents, onOpenCalendarCreate,
}: CalendarWidgetBodyProps): React.JSX.Element {
  const cfg = useMemo<Partial<CalendarConfig>>(() => widget.config ?? {}, [widget.config]);
  const activeFilters = countActiveCalendarFilters(cfg);

  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day' | 'agenda'>('month');
  const [currentDate, setCurrentDate] = useState<Date>((): Date => new Date(2026, 6, 12));
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const events = useMemo((): CalendarEvent[] => applyCalendarFilters(calendarEvents, cfg), [calendarEvents, cfg]);

  const navigateDate = (dir: 'prev' | 'next'): void => {
    setCurrentDate((prev) => {
      const copy = new Date(prev.getTime());
      if (currentView === 'month') {
        copy.setMonth(copy.getMonth() + (dir === 'next' ? 1 : -1));
      } else if (currentView === 'week') {
        copy.setDate(copy.getDate() + (dir === 'next' ? 7 : -7));
      } else if (currentView === 'day') {
        copy.setDate(copy.getDate() + (dir === 'next' ? 1 : -1));
      }
      return copy;
    });
  };

  const monthData = useMemo((): (Date | null)[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const numDays = new Date(year, month + 1, 0).getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayIndex; i++) {
      days.push(null);
    }
    for (let i = 1; i <= numDays; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, [currentDate]);

  const weekData = useMemo((): Date[] => {
    const startOfWeek = new Date(currentDate.getTime());
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    return Array.from({ length: 7 }).map((_, i) => {
      const day = new Date(startOfWeek.getTime());
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });
  }, [currentDate]);

  const getEventTime = (iso: string): string => {
    if (!iso.includes('T')) return 'All Day';
    try {
      const date = new Date(iso);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const renderCellEvents = (day: Date): React.JSX.Element => {
    const dayStr = day.toISOString().slice(0, 10);
    const dayEvents = events.filter((e) => e.start.startsWith(dayStr));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
        {dayEvents.map((e) => {
          const typeDef = CALENDAR_TYPE_DEFS[e.type];
          return (
            <div
              key={e.id}
              onClick={(evt) => { evt.stopPropagation(); setSelectedEvent(e); }}
              style={{
                fontSize: '12px', padding: '1px 3px', borderRadius: '4px',
                background: typeDef.bg, color: typeDef.color, fontWeight: '600',
                borderLeft: `3px solid ${typeDef.color}`,
                cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}
              title={e.title}
            >
              {e.title}
            </div>
          );
        })}
      </div>
    );
  };

  const handleDeleteEvent = async (eventId: string): Promise<void> => {
    if (await confirmAction('Delete this event?')) {
      onUpdateCalendarEvents(calendarEvents.filter((e) => e.id !== eventId));
      setSelectedEvent(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {/* Calendar Header toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '4px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Button variant="secondary" type="submit" size="sm" onClick={(): void => navigateDate('prev')} >◀</Button>
          <span style={{ fontSize: '12px', fontWeight: 'bold', minWidth: '95px', textAlign: 'center' }}>
            {currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
          <Button variant="secondary" type="submit" size="sm" onClick={(): void => navigateDate('next')} >▶</Button>
          {activeFilters > 0 && (
            <span style={{ fontSize: '12px', background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: '10px', padding: '2px 7px', fontWeight: 700 }}>
              {activeFilters} filter{activeFilters > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '2px', background: 'var(--page)', padding: '2px', borderRadius: '6px', border: '1px solid var(--line)' }}>
            {(['month', 'week', 'day', 'agenda'] as const).map((view) => (
              <button
                key={view}
                onClick={(): void => setCurrentView(view)}
                style={{
                  padding: '3px 7px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer',
                  background: currentView === view ? 'var(--card)' : 'transparent',
                  color: currentView === view ? 'var(--primary)' : 'var(--muted)',
                  boxShadow: currentView === view ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                {view.toUpperCase()}
              </button>
            ))}
          </div>
          <Button variant="primary" type="submit" size="sm"
            onClick={onOpenCalendarCreate}
            style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
          >
            ＋ New
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        
        {/* Month View */}
        {currentView === 'month' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderLeft: '1px solid var(--line)', borderTop: '1px solid var(--line)', height: '100%', minHeight: '180px' }}>
            {['S','M','T','W','T','F','S'].map((day, idx) => (
              <div key={idx} style={{ textAlign: 'center', fontSize: '12px', fontWeight: 'bold', color: 'var(--muted)', padding: '2px 0', borderRight: '1px solid var(--line)', borderBottom: '1px solid var(--line)', background: 'var(--page)' }}>{day}</div>
            ))}
            {monthData.map((day, idx) => (
              <div key={idx} style={{
                borderRight: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
                minHeight: '28px', padding: '2px', background: day ? 'var(--card)' : 'var(--page)',
                display: 'flex', flexDirection: 'column', gap: '2px'
              }}>
                {day && (
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: day.toDateString() === new Date(2026, 6, 12).toDateString() ? 'var(--primary)' : 'var(--ink)' }}>
                    {day.getDate()}
                  </span>
                )}
                {day && renderCellEvents(day)}
              </div>
            ))}
          </div>
        )}

        {/* Week View */}
        {currentView === 'week' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', height: '100%', minHeight: '120px' }}>
            {weekData.map((day, idx) => {
              const dayStr = day.toISOString().slice(0, 10);
              const dayEvents = events.filter((e) => e.start.startsWith(dayStr));
              return (
                <div key={idx} style={{ background: 'var(--page)', border: '1px solid var(--line)', borderRadius: '6px', padding: '4px', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', textAlign: 'center', borderBottom: '1px solid var(--line)', paddingBottom: '2px', marginBottom: '4px' }}>
                    {day.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                  </span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
                    {dayEvents.map((e) => (
                      <div key={e.id} onClick={(): void => setSelectedEvent(e)}
                        style={{ fontSize: '12px', padding: '3px', borderRadius: '4px', background: CALENDAR_TYPE_DEFS[e.type].bg, color: CALENDAR_TYPE_DEFS[e.type].color, borderLeft: `3px solid ${CALENDAR_TYPE_DEFS[e.type].color}`, fontWeight: '600', cursor: 'pointer' }}>
                        <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</div>
                        <div style={{ opacity: 0.8, fontSize: '12px' }}>{getEventTime(e.start)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Day View */}
        {currentView === 'day' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', color: 'var(--ink)' }}>
              Events for {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
            {((): React.JSX.Element | React.JSX.Element[] => {
              const dayStr = currentDate.toISOString().slice(0, 10);
              const dayEvents = events.filter((e) => e.start.startsWith(dayStr));
              if (dayEvents.length === 0) return <div style={{ color: 'var(--muted)', fontSize: '12px', padding: '12px', textAlign: 'center' }}>No events scheduled for today.</div>;
              return dayEvents.map((e) => (
                <div key={e.id} onClick={(): void => setSelectedEvent(e)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: CALENDAR_TYPE_DEFS[e.type].bg, borderLeft: `4px solid ${CALENDAR_TYPE_DEFS[e.type].color}`, borderRadius: '6px', cursor: 'pointer' }}>
                  <span style={{ fontSize: '14px' }}>{CALENDAR_TYPE_DEFS[e.type].icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: CALENDAR_TYPE_DEFS[e.type].color }}>{e.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{getEventTime(e.start)} | {e.calendarName}</div>
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        {/* Agenda View */}
        {currentView === 'agenda' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {events.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: '12px', padding: '24px 0', textAlign: 'center' }}>
                No upcoming events found matching filters.
              </div>
            ) : (
              events.map((e) => (
                <div key={e.id} onClick={(): void => setSelectedEvent(e)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: 'var(--page)', border: '1px solid var(--line)', borderRadius: '8px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px' }}>{CALENDAR_TYPE_DEFS[e.type].icon}</span>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--ink)' }}>{e.title}</div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                        {new Date(e.start).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {getEventTime(e.start)}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '12px', background: CALENDAR_TYPE_DEFS[e.type].bg, color: CALENDAR_TYPE_DEFS[e.type].color, padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                    {e.calendarName}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {selectedEvent && (
        <div style={{ position: 'absolute', bottom: '10px', left: '10px', right: '10px', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: '8px', padding: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: CALENDAR_TYPE_DEFS[selectedEvent.type].color }}>
              {CALENDAR_TYPE_DEFS[selectedEvent.type].icon} {selectedEvent.title}
            </span>
            <Button variant="ghost" type="submit" size="sm" onClick={(): void => setSelectedEvent(null)} >✕</Button>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink)' }}>
            <strong>Calendar:</strong> {selectedEvent.calendarName} | <strong>Creator:</strong> {selectedEvent.creator}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--ink)', marginTop: '4px' }}>
            <strong>Time:</strong> {new Date(selectedEvent.start).toLocaleString()}
          </div>
          {selectedEvent.location && (
            <div style={{ fontSize: '12px', color: 'var(--ink)', marginTop: '2px' }}>
              <strong>Location:</strong> {selectedEvent.location}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '6px', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--muted)', flex: 1, marginRight: '10px' }}>
              {(nonEmptyString(selectedEvent.description) ?? 'No description provided.')}
            </div>
            {calendarEvents.some((ce) => ce.id === selectedEvent.id) && (
              <Button variant="ghost" type="submit" size="sm"
                onClick={(): void => void handleDeleteEvent(selectedEvent.id)}

              >
                🗑️ Delete Event
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CostWidgetConfig sub-component ──────────────────────────────────────────
interface CostWidgetConfigProps {
  widget: WidgetPlacement;
  onSave: (cfg: CostConfig) => void;
  onCancel: () => void;
}

function CostWidgetConfig({ widget, onSave, onCancel }: CostWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState<CostConfig>((): { query?: string; pageSize?: 5 | 10 | 20 | 50; fileTypes?: string[]; parentFolderId?: string; createdAfter?: string; createdBefore?: string; updatedAfter?: string; updatedBefore?: string; createdBy?: string; sortDir?: "asc" | "desc"; status?: "all" | "pending" | "in_progress" | "completed"; filterKeyword?: string; assignedTo?: string; dueAfter?: string; dueBefore?: string; enabledActions?: string[]; customLinks?: import("../lib/widgetConfig").CustomActionLink[]; eventTypes?: string[]; calendarName?: string; dateAfter?: string; dateBefore?: string; feesBudget: number; expensesBudget: number; targets: CostTargetConfig[]; sortField?: DocConfig["sortField"] | TaskConfig["sortField"]; showArchived?: boolean; authorNameFilter?: string; } => {
    const base = { ...COST_CONFIG_DEFAULT, ...widget.config };
    if (!base.targets || base.targets.length === 0) {
      base.targets = [...COST_CONFIG_DEFAULT.targets];
    }
    return base;
  });

  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);

  const setBudget = (key: 'feesBudget' | 'expensesBudget', val: number): void => {
    setForm(p => ({ ...p, [key]: val }));
  };

  const updateTarget = (id: string, fields: Partial<CostTargetConfig>): void => {
    setForm(p => ({
      ...p,
      targets: p.targets.map(t => t.id === id ? { ...t, ...fields } : t)
    }));
  };

  const handleAddTarget = (): void => {
    const newTarget: CostTargetConfig = {
      id: `target_${Date.now()}`,
      name: 'New Category',
      type: 'TimeEntry',
      categoryFilter: '',
      userFilter: '',
      dateAfter: '',
      dateBefore: '',
      targetBudget: 1000
    };
    setForm(p => ({
      ...p,
      targets: [...p.targets, newTarget]
    }));
    setExpandedTargetId(newTarget.id);
  };

  const handleRemoveTarget = (id: string): void => {
    setForm(p => ({
      ...p,
      targets: p.targets.filter(t => t.id !== id)
    }));
  };

  const handleDragStart = (e: React.DragEvent, index: number): void => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number): void => {
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(sourceIndex) || sourceIndex === targetIndex) return;

    const reordered = [...form.targets];
    const removed = reordered.splice(sourceIndex, 1)[0];
    if (removed) {
      reordered.splice(targetIndex, 0, removed);
    }

    setForm(p => ({
      ...p,
      targets: reordered
    }));
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px' }}>
      <strong style={{ fontSize: '13px', marginBottom: '8px' }}>Configure Cost &amp; Budgets</strong>
      
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div>
            <label style={labelStyle}>Legal Fees Budget ($)</label>
            <input type="number" value={form.feesBudget} onChange={(e) => setBudget('feesBudget', Number(e.target.value))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Expenses Budget ($)</label>
            <input type="number" value={form.expensesBudget} onChange={(e) => setBudget('expensesBudget', Number(e.target.value))} style={inputStyle} />
          </div>
        </div>

        <strong style={{ display: 'block', fontSize: '12px', color: 'var(--ink)', marginTop: '8px', marginBottom: '8px', borderBottom: '1px solid var(--line)', paddingBottom: '4px' }}>
          🎯 Live Categories &amp; Targets
        </strong>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {form.targets.map((t, idx) => {
            const isExpanded = expandedTargetId === t.id;
            return (
              <div 
                key={t.id}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, idx)}
                style={{ border: '1px solid var(--line)', borderRadius: '8px', background: 'var(--card)', overflow: 'hidden' }}
              >
                <div 
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--page)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'grab' }}>
                    <span style={{ color: 'var(--muted)', fontSize: '14px', marginRight: '4px', cursor: 'grab', userSelect: 'none' }}>☰</span>
                    <span 
                      onClick={(): void => setExpandedTargetId(isExpanded ? null : t.id)}
                      style={{ fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
                    >
                      {t.name} {t.type === 'TimeEntry' ? '⏱️' : '📦'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span 
                      onClick={(): void => setExpandedTargetId(isExpanded ? null : t.id)}
                      style={{ fontSize: '12px', color: 'var(--muted)', cursor: 'pointer' }}
                    >
                      Budget: ${t.targetBudget.toLocaleString()}
                    </span>
                    <Button variant="ghost" size="sm"
                      type="button"
                      onClick={(): void => handleRemoveTarget(t.id)}

                      title="Remove Category"
                    >
                      🗑️
                    </Button>
                    <span 
                      onClick={(): void => setExpandedTargetId(isExpanded ? null : t.id)}
                      style={{ fontSize: '12px', cursor: 'pointer' }}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '12px', borderTop: '1px solid var(--line)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', background: 'var(--card)' }}>
                    <div>
                      <label style={labelStyle}>Category Name</label>
                      <input type="text" value={t.name} onChange={(e) => updateTarget(t.id, { name: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Target Budget ($)</label>
                      <input type="number" value={t.targetBudget} onChange={(e) => updateTarget(t.id, { targetBudget: Number(e.target.value) })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Type Filter</label>
                      <Select value={t.type} onChange={(e) => updateTarget(t.id, { type: e.target.value as "all" | "TimeEntry" | "ExpenseEntry" })} >
                        <option value="TimeEntry">Time Entry (Hours)</option>
                        <option value="ExpenseEntry">Expense / Cost</option>
                        <option value="all">All Activities</option>
                      </Select>
                    </div>
                    <div>
                      <label style={labelStyle}>Category Name Filter</label>
                      <input type="text" value={(t.categoryFilter ?? '')} placeholder="e.g. Partner, Filing" onChange={(e) => updateTarget(t.id, { categoryFilter: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Billed By (User Name)</label>
                      <input type="text" value={(t.userFilter ?? '')} placeholder="e.g. Nicholas" onChange={(e) => updateTarget(t.id, { userFilter: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                      <div>
                        <label style={labelStyle}>Billed After</label>
                        <input type="date" value={(t.dateAfter ?? '')} onChange={(e) => updateTarget(t.id, { dateAfter: e.target.value })} style={{ ...inputStyle, fontSize: '12px' }} />
                      </div>
                      <div>
                        <label style={labelStyle}>Billed Before</label>
                        <input type="date" value={(t.dateBefore ?? '')} onChange={(e) => updateTarget(t.id, { dateBefore: e.target.value })} style={{ ...inputStyle, fontSize: '12px' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button variant="secondary" size="sm"
          type="button"
          onClick={handleAddTarget}
          style={{ marginTop: '12px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginBottom: '8px' }}
        >
          ＋ Add Target Category
        </Button>
      </div>

      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid var(--line)', marginTop: '8px' }}>
        <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit"  onClick={(): void => onSave(form)}>Save</Button>
      </div>
    </div>
  );
}

// ─── CostWidgetBody sub-component ────────────────────────────────────────────
interface CostWidgetBodyProps {
  widget: WidgetPlacement;
  clioActivities: ClioActivity[];
}

function CostWidgetBody({ widget, clioActivities }: CostWidgetBodyProps): React.JSX.Element {
  const cfg = useMemo((): { query?: string; pageSize?: 5 | 10 | 20 | 50; fileTypes?: string[]; parentFolderId?: string; createdAfter?: string; createdBefore?: string; updatedAfter?: string; updatedBefore?: string; createdBy?: string; sortDir?: "asc" | "desc"; status?: "all" | "pending" | "in_progress" | "completed"; filterKeyword?: string; assignedTo?: string; dueAfter?: string; dueBefore?: string; enabledActions?: string[]; customLinks?: import("../lib/widgetConfig").CustomActionLink[]; eventTypes?: string[]; calendarName?: string; dateAfter?: string; dateBefore?: string; feesBudget: number; expensesBudget: number; targets: CostTargetConfig[]; sortField?: DocConfig["sortField"] | TaskConfig["sortField"]; showArchived?: boolean; authorNameFilter?: string; } => {
    const base = { ...COST_CONFIG_DEFAULT, ...widget.config };
    if (!base.targets || base.targets.length === 0) {
      base.targets = [...COST_CONFIG_DEFAULT.targets];
    }
    return base;
  }, [widget.config]);

  // Aggregate total spent for time entries vs expense entries
  const totalFeesSpent = useMemo((): number => {
    return clioActivities
      .filter(act => act.type === 'TimeEntry')
      .reduce((sum, act) => sum + act.total, 0);
  }, [clioActivities]);

  const totalExpSpent = useMemo((): number => {
    return clioActivities
      .filter(act => act.type === 'ExpenseEntry')
      .reduce((sum, act) => sum + act.total, 0);
  }, [clioActivities]);

  const totalBudget = cfg.feesBudget + cfg.expensesBudget;
  const totalSpent = totalFeesSpent + totalExpSpent;

  const feesPct = Math.min(100, Math.round((totalFeesSpent / cfg.feesBudget) * 100)) ?? 0;
  const expPct = Math.min(100, Math.round((totalExpSpent / cfg.expensesBudget) * 100)) ?? 0;
  const totalPct = Math.min(100, Math.round((totalSpent / totalBudget) * 100)) ?? 0;

  const progressBar = (spent: number, budget: number, pct: number, label: string): React.JSX.Element => {
    const color = pct >= 90 ? 'var(--t0)' : pct >= 75 ? 'var(--t1)' : 'var(--t4)';
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
          <span style={{ fontWeight: 'bold' }}>{label}</span>
          <span style={{ color: 'var(--muted)' }}>${spent.toLocaleString()} / ${budget.toLocaleString()} ({pct}%)</span>
        </div>
        <div style={{ height: '6px', background: 'var(--page)', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
        </div>
      </div>
    );
  };

  const showTable = widget.h >= 2 || widget.w >= 2;

  const tableRow = (label: string, budget: number, spent: number): React.JSX.Element => {
    const variance = budget - spent;
    const isOver = variance < 0;
    return (
      <tr key={label} style={{ fontSize: '12px', borderBottom: '1px solid var(--line-soft)' }}>
        <td style={{ padding: '4px 0', color: 'var(--ink)' }}>{label}</td>
        <td style={{ padding: '4px 0', textAlign: 'right' }}>${budget.toLocaleString()}</td>
        <td style={{ padding: '4px 0', textAlign: 'right' }}>${spent.toLocaleString()}</td>
        <td style={{ padding: '4px 0', textAlign: 'right', color: isOver ? 'var(--t0)' : 'var(--t4)', fontWeight: 'bold' }}>
          {isOver ? '-' : ''}${Math.abs(variance).toLocaleString()}
        </td>
      </tr>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '10px' }}>
      {/* 1. Stacked Budgets Progress Rows */}
      <div style={{ display: 'flex', flexDirection: widget.w >= 3 ? 'row' : 'column', gap: '10px', flexShrink: 0 }}>
        {progressBar(totalFeesSpent, cfg.feesBudget, feesPct, '⚖️ Legal Fees')}
        {progressBar(totalExpSpent, cfg.expensesBudget, expPct, '📦 Expenses')}
      </div>

      {/* 2. Total Matter Summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--page)', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--line)', flexShrink: 0, fontSize: '12px' }}>
        <span>Total Matter Cost: <b>${totalSpent.toLocaleString()} spent</b></span>
        <span style={{ color: totalPct >= 90 ? 'var(--t0)' : 'var(--muted)', fontWeight: 'bold' }}>
          Total Burn: {totalPct}% (Budget: ${totalBudget.toLocaleString()})
        </span>
      </div>

      {/* 3. Detailed Financial Breakdown Grid/Table */}
      {showTable && (
        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: '6px', padding: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ fontSize: '12px', color: 'var(--muted)', borderBottom: '1px solid var(--line)', textTransform: 'uppercase' }}>
                <th style={{ textAlign: 'left', paddingBottom: '3px' }}>Category</th>
                <th style={{ textAlign: 'right', paddingBottom: '3px' }}>Budget</th>
                <th style={{ textAlign: 'right', paddingBottom: '3px' }}>Spent</th>
                <th style={{ textAlign: 'right', paddingBottom: '3px' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {cfg.targets.map((t: CostTargetConfig) => {
                const spent = sumMatchingActivities(clioActivities, t);
                return tableRow(t.name, t.targetBudget, spent);
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Helpers: layout ─────────────────────────────────────────────────────────

function widgetsToLayout(widgets: WidgetPlacement[]): Layout {
  const placed: LayoutItem[] = [];
  let cursor = { x: 0, y: 0 };

  for (const w of widgets) {
    let x = w.x ?? -1;
    let y = w.y ?? -1;

    if (x < 0 || y < 0) {
      let found = false;
      for (let row = 0; row <= MAX_ROWS - w.h && !found; row++) {
        for (let col = 0; col <= COLS - w.w && !found; col++) {
          const candidate = { x: col, y: row, w: w.w, h: w.h };
          const collides = placed.some(
            (p) =>
              candidate.x < p.x + p.w &&
              candidate.x + candidate.w > p.x &&
              candidate.y < p.y + p.h &&
              candidate.y + candidate.h > p.y
          );
          if (!collides) { x = col; y = row; found = true; }
        }
      }
      if (!found) { x = cursor.x; y = cursor.y; }
    }

    const item: LayoutItem = {
      i: w.id,
      x: Math.min(x, COLS - w.w),
      y: Math.min(y, MAX_ROWS - w.h),
      w: Math.min(w.w, COLS),
      h: Math.min(w.h, MAX_ROWS),
      minW: 1, minH: 1, maxW: COLS, maxH: MAX_ROWS,
    };
    placed.push(item);
    cursor = { x: (item.x + item.w) % COLS, y: item.y + (item.x + item.w >= COLS ? item.h : 0) };
  }
  return placed;
}

function mergeLayoutIntoWidgets(layout: Layout, widgets: WidgetPlacement[]): WidgetPlacement[] {
  return layout.map((item) => {
    const original = widgets.find((w) => w.id === item.i);
    if (!original) return null;
    return { ...original, x: item.x, y: item.y, w: item.w, h: item.h };
  }).filter(Boolean) as WidgetPlacement[];
}

// ─── StageBoard ───────────────────────────────────────────────────────────────
export function StageBoard({
  activeStageId, widgets, clioTasks, clioDocuments,
  onOpenModal, onToggleTaskStatus, 
  stageName, stageDesc, onUpdateWidgetConfig, onSaveLayout, matterId,
  hasSavedLayout = true,
  strategicNotes, onUpdateStrategicNotes,
  calendarEvents, onUpdateCalendarEvents, onAddCalendarEvent,
  clioActivities,
}: StageBoardProps): React.JSX.Element {

  // ── Edit mode ────────────────────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  const [workspaceTemplates, setWorkspaceTemplates] = useState<WorkspaceTemplate[]>([]);
  const [defaultTemplateId, setDefaultTemplateId] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateError, setTemplateError] = useState('');
  const [templateDialog, setTemplateDialog] = useState<
    { kind: 'save' } | { kind: 'delete'; template: WorkspaceTemplate } | null
  >(null);
  const appliedDefaultForStage = useRef<string | null>(null);

  // ── RGL layout ───────────────────────────────────────────────────────────
  const [layout, setLayout] = useState<Layout>((): Layout => widgetsToLayout(widgets));
  const [localWidgets, setLocalWidgets] = useState<WidgetPlacement[]>(widgets);
  const [gridGap, setGridGap] = useState(DEFAULT_GRID_GAP);
  const localWidgetsRef = useRef<WidgetPlacement[]>(widgets);
  const onSaveLayoutRef = useRef(onSaveLayout);

  const [layoutSource, setLayoutSource] = useState({ activeStageId, widgets });
  if (layoutSource.activeStageId !== activeStageId || layoutSource.widgets !== widgets) {
    setLayoutSource({ activeStageId, widgets });
    setLocalWidgets(widgets);
    setLayout(widgetsToLayout(widgets));
    const savedGap = widgets.find(widget => widget.grid_gap)?.grid_gap;
    if (savedGap) setGridGap(Math.min(24, Math.max(8, savedGap)));
    else if (layoutSource.activeStageId !== activeStageId) setGridGap(DEFAULT_GRID_GAP);
  }
  useEffect((): void => { localWidgetsRef.current = widgets; }, [activeStageId, widgets]);

  useEffect((): void => {
    onSaveLayoutRef.current = onSaveLayout;
  }, [onSaveLayout]);

  const applyTemplate = useCallback((template: WorkspaceTemplate, persist = true) => {
    const instanceToken = Date.now().toString(36);
    const applied = template.widgets.map((widget, index) => ({
      ...widget,
      id: `${template.id}_${activeStageId}_${index}_${instanceToken}`,
      grid_gap: nonZeroNumber(template.grid_gap) ?? DEFAULT_GRID_GAP,
      config: widget.config == null ? undefined : structuredClone(widget.config),
    }));
    localWidgetsRef.current = applied;
    setLocalWidgets(applied);
    setLayout(widgetsToLayout(applied));
    setGridGap(Math.min(24, Math.max(8, nonZeroNumber(template.grid_gap) ?? DEFAULT_GRID_GAP)));
    setTemplateMenuOpen(false);
    setEditMode(false);
    if (persist) onSaveLayoutRef.current?.(activeStageId, applied);
  }, [activeStageId]);

  const fetchTemplateLibrary = useCallback(async () => {
    try {
      const library = await listWorkspaceTemplates();
      setTemplateError('');
      setWorkspaceTemplates((library.templates ?? []));
      setDefaultTemplateId((library.default_template_id ?? ''));
      return library;
    } catch (error) {
      setTemplateError(error instanceof Error ? errorMessage(error) : 'Unable to load templates.');
      return null;
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const refreshTemplateLibrary = useCallback(() => {
    setTemplatesLoading(true);
    setTemplateError('');
    return fetchTemplateLibrary();
  }, [fetchTemplateLibrary]);

  useEffect((): () => void => {
    let active = true;
    void fetchTemplateLibrary().then((library) => {
      if (!active || !library || hasSavedLayout || appliedDefaultForStage.current === activeStageId) return;
      const selectedDefault = library.templates.find((template) => template.id === library.default_template_id);
      if (selectedDefault) {
        appliedDefaultForStage.current = activeStageId;
        applyTemplate(selectedDefault);
      }
    });
    return (): void => { active = false; };
  }, [activeStageId, applyTemplate, hasSavedLayout, fetchTemplateLibrary]);

  // ── Container width ──────────────────────────────────────────────────────
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 900 });

  // ── Widget config panel ───────────────────────────────────────────────────
  const [openConfigWidgetId, setOpenConfigWidgetId] = useState<string | null>(null);
  const [calendarCreateOpen, setCalendarCreateOpen] = useState(false);

  // ── Document widget pagination (per-widget page counter) ──────────────────
  const [docPages, setDocPages] = useState<Record<string, number>>({});

  // ── Template launcher modal ───────────────────────────────────────────────
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean; title: string; message?: string; type: 'alert'; onConfirm: () => void;
  }>({ isOpen: false, title: '', type: 'alert', onConfirm: (): undefined => undefined });



  // Close palette on outside click
  useEffect((): (() => void) | undefined => {
    if (!paletteOpen && !templateMenuOpen) return;
    const handler = (e: MouseEvent): void => {
      if (paletteRef.current && !paletteRef.current.contains(e.target as Node)) {
        setPaletteOpen(false);
      }
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target as Node)) {
        setTemplateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => document.removeEventListener('mousedown', handler);
  }, [paletteOpen, templateMenuOpen]);

  // ── Layout change handler ─────────────────────────────────────────────────
  const handleLayoutChange = useCallback((newLayout: Layout) => {
    setLayout(newLayout);
    setLocalWidgets((current) => {
      const merged = mergeLayoutIntoWidgets(newLayout, current);
      localWidgetsRef.current = merged;
      return merged;
    });
  }, []);

  // react-grid-layout emits onLayoutChange repeatedly while dragging and also
  // when callback identities change. Persist only the final layout so one user
  // interaction produces one graph write and one template write.
  const persistFinalLayout = useCallback((finalLayout: Layout) => {
    const merged = mergeLayoutIntoWidgets(finalLayout, localWidgetsRef.current);
    localWidgetsRef.current = merged;
    setLayout(finalLayout);
    setLocalWidgets(merged);
    onSaveLayoutRef.current?.(activeStageId, merged);
  }, [activeStageId]);

  // ── Add widget ────────────────────────────────────────────────────────────
  const handleAddWidget = (def: WidgetDef): void => {
    const newId = genPoolId('widget');
    const newWidget: WidgetPlacement = {
      id: newId, type: def.type,
      w: Math.min(def.defaultW, COLS), h: Math.min(def.defaultH, MAX_ROWS),
      grid_gap: gridGap,
    };
    const updatedWidgets = [...localWidgets, newWidget];
    const updatedLayout = widgetsToLayout(updatedWidgets);
    const positionedWidgets = mergeLayoutIntoWidgets(updatedLayout, updatedWidgets);
    localWidgetsRef.current = positionedWidgets;
    setLocalWidgets(positionedWidgets);
    setLayout(updatedLayout);
    onSaveLayoutRef.current?.(activeStageId, positionedWidgets);
    setPaletteOpen(false);
  };

  // ── Remove widget ─────────────────────────────────────────────────────────
  const handleRemoveWidget = (widgetId: string): void => {
    const updatedWidgets = localWidgets.filter((w) => w.id !== widgetId);
    const updatedLayout  = layout.filter((item) => item.i !== widgetId);
    const positionedWidgets = mergeLayoutIntoWidgets(updatedLayout, updatedWidgets);
    localWidgetsRef.current = positionedWidgets;
    setLocalWidgets(positionedWidgets);
    setLayout(updatedLayout);
    if (openConfigWidgetId === widgetId) setOpenConfigWidgetId(null);
    onSaveLayoutRef.current?.(activeStageId, positionedWidgets);
  };

  const handleSaveTemplate = async (values?: Record<string, string>): Promise<void> => {
    const name = values?.name?.trim();
    if (!name) return;
    setTemplateDialog(null);
    try {
      const positionedWidgets = mergeLayoutIntoWidgets(layout, localWidgetsRef.current);
      const saved = await saveWorkspaceTemplate({
        name,
        description: (nonEmptyString(values?.description?.trim()) ?? `Saved from the ${stageName} workspace.`),
        grid_gap: gridGap,
        widgets: positionedWidgets,
      });
      setWorkspaceTemplates((current) => [...current, saved]);
      setTemplateMenuOpen(true);
    } catch (error) {
      setTemplateError(error instanceof Error ? errorMessage(error) : 'Unable to save template.');
      setTemplateMenuOpen(true);
    }
  };

  const handleSetDefaultTemplate = async (template: WorkspaceTemplate): Promise<void> => {
    setTemplateError('');
    try {
      const selected = await setDefaultWorkspaceTemplate(template.id);
      setDefaultTemplateId(selected);
    } catch (error) {
      setTemplateError(error instanceof Error ? errorMessage(error) : 'Unable to set the default template.');
    }
  };

  const handleDeleteTemplate = async (template: WorkspaceTemplate): Promise<void> => {
    setTemplateDialog(null);
    try {
      await deleteWorkspaceTemplate(template.id);
      if (defaultTemplateId === template.id) {
        await refreshTemplateLibrary();
      } else {
        setWorkspaceTemplates((current) => current.filter((item) => item.id !== template.id));
      }
      setTemplateMenuOpen(true);
    } catch (error) {
      setTemplateError(error instanceof Error ? errorMessage(error) : 'Unable to delete template.');
      setTemplateMenuOpen(true);
    }
  };



  // ── Widget config form renderer ───────────────────────────────────────────
  const renderWidgetConfigForm = (widget: WidgetPlacement): React.JSX.Element => {
    const handleSaveConfig = (newConfig: WidgetConfig): void => {
      onUpdateWidgetConfig(widget.id, newConfig);
      setOpenConfigWidgetId(null);
    };

    switch (widget.type) {

      case 'document':
        return (
          <DocWidgetConfig
            widget={widget}
            clioDocuments={clioDocuments}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'task':
        return (
          <TaskWidgetConfig
            widget={widget}
            clioTasks={clioTasks}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'action':
        return (
          <ActionWidgetConfig
            widget={widget}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'calendar':
        return (
          <CalendarWidgetConfig
            widget={widget}
            allEvents={calendarEvents}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'cost':
        return (
          <CostWidgetConfig
            widget={widget}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'note':
        return (
          <NotesWidgetConfig
            widget={widget}
            onSave={handleSaveConfig}
            onCancel={(): void => setOpenConfigWidgetId(null)}
          />
        );

      case 'template_launch':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', fontSize: '12.5px' }}>
            <strong>Configure Widget</strong>
            <p style={{ color: 'var(--muted)' }}>No customizable settings for this widget type.</p>
            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" type="submit"  onClick={(): void => setOpenConfigWidgetId(null)}>Close</Button>
            </div>
          </div>
        );
    }
  };

  // ── Widget body renderer ──────────────────────────────────────────────────
  const renderWidgetBody = (widget: WidgetPlacement): React.JSX.Element => {
    switch (widget.type) {

      case 'action':
        return (
          <ActionWidgetBody
            widget={widget}
            onOpenModal={onOpenModal}
            matterId={matterId}
          />
        );

      case 'document':
        return (
          <DocWidgetBody
            widget={widget}
            clioDocuments={clioDocuments}
            page={nonZeroNumber(docPages[widget.id]) ?? 1}
            onLoadMore={(): void => setDocPages((p) => ({ ...p, [widget.id]: (nonZeroNumber(p[widget.id]) ?? 1) + 1 }))}
          />
        );

      case 'task':
        return (
          <TaskWidgetBody
            widget={widget}
            clioTasks={clioTasks}
            onToggleTaskStatus={onToggleTaskStatus}
            activeStageId={activeStageId}
          />
        );

      case 'calendar':
        return (
          <CalendarWidgetBody
            widget={widget}
            calendarEvents={calendarEvents}
            onUpdateCalendarEvents={onUpdateCalendarEvents}
            onOpenCalendarCreate={(): void => setCalendarCreateOpen(true)}
          />
        );

      case 'cost':
        return (
          <CostWidgetBody
            widget={widget}
            clioActivities={clioActivities}
          />
        );

      case 'note':
        return (
          <NotesWidgetBody
            widget={widget}
            notes={strategicNotes}
            onUpdateNotes={onUpdateStrategicNotes}
          />
        );

      case 'template_launch':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="ct" style={{ marginBottom: '8px' }}>📄 Clio Template Launcher</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, justifyContent: 'center' }}>
              {[{ name: 'Written Discovery Template', id: 't_wd' }, { name: 'Deposition Notice Template', id: 't_dn' }, { name: 'Tolling Agreement Template', id: 't_ta' }].map((tpl) => (
                <div key={tpl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px', background: 'var(--page)', borderRadius: '6px', border: '1px solid var(--line)', fontSize: '12px' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{tpl.name}</span>
                  <button onClick={(): void => setModalConfig({ isOpen: true, title: 'Document Creator', message: `Generated "${tpl.name}" in Clio!`, type: 'alert', onConfirm: (): void => setModalConfig((p) => ({ ...p, isOpen: false })) })}
                    style={{ background: 'var(--primary-soft)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>Launch</button>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div>Unknown Widget Type</div>;
    }
  };

  // ── Widget label lookup ───────────────────────────────────────────────────
  const getWidgetLabel = (type: WidgetPlacement['type']): string => {
    const labels: Record<WidgetPlacement['type'], string> = {
      action: '⚡ Action Toolbar', document: '📂 Document Pipeline', task: '📋 Clio Tasks',
      cost: '💰 Cost & Budgets', calendar: '📅 Calendar', note: '📝 Strategic Notes', template_launch: '📄 Template Creator',
    };
    return (nonEmptyString(labels[type]) ?? type);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="view show">
      {/* Header */}
      <div className="vhead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2>Stage Workspace: {stageName}</h2>
          <div className="desc">{stageDesc}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div ref={templateMenuRef} className="workspace-template-control">
            <button
              id="workspace-templates-btn"
              className="workspace-template-button"
              onClick={(): void => {
                setTemplateMenuOpen((open) => !open);
                setPaletteOpen(false);
              }}
            >
              <span>▦</span> Templates
            </button>
            {templateMenuOpen && (
              <div className="workspace-template-menu">
                <div className="workspace-template-menu-header">
                  <div>
                    <strong>Workspace templates</strong>
                    <span>Load a layout or save this one for later.</span>
                  </div>
                  <button
                    className="workspace-template-save"
                    onClick={(): void => setTemplateDialog({ kind: 'save' })}
                    disabled={localWidgets.length === 0}
                  >
                    + Save current
                  </button>
                </div>

                {templateError && <div className="workspace-template-error">{templateError}</div>}
                {templatesLoading ? (
                  <div className="workspace-template-empty">Loading templates…</div>
                ) : workspaceTemplates.length === 0 ? (
                  <div className="workspace-template-empty">No templates are available yet.</div>
                ) : (
                  <div className="workspace-template-list">
                    {(['global', 'local'] as const).map((scope) => {
                      const scoped = workspaceTemplates.filter((template) => template.scope === scope);
                      if (scoped.length === 0) return null;
                      return (
                        <section key={scope}>
                          <div className="workspace-template-scope">
                            {scope === 'global' ? 'Lattice templates' : 'My templates'}
                          </div>
                          {scoped.map((template) => {
                            const isDefault = template.id === defaultTemplateId;
                            return (
                              <div className="workspace-template-item" key={template.id}>
                                <div className="workspace-template-copy">
                                  <div className="workspace-template-name">
                                    {template.name}
                                    {isDefault && <span className="workspace-template-default-badge">Default</span>}
                                  </div>
                                  <p>{(nonEmptyString(template.description) ?? `${template.widgets.length} widget layout`)}</p>
                                  <span>{template.widgets.length} widgets · {nonZeroNumber(template.grid_gap) ?? DEFAULT_GRID_GAP}px spacing</span>
                                </div>
                                <div className="workspace-template-actions">
                                  <button className="workspace-template-load" onClick={(): void => applyTemplate(template)}>
                                    Load
                                  </button>
                                  <button
                                    className="workspace-template-secondary"
                                    onClick={(): void => void handleSetDefaultTemplate(template)}
                                    disabled={isDefault}
                                    title="Use automatically for workspaces without a saved layout"
                                  >
                                    {isDefault ? 'Default' : 'Make default'}
                                  </button>
                                  {template.scope === 'local' && (
                                    <button
                                      className="workspace-template-delete"
                                      onClick={(): void => setTemplateDialog({ kind: 'delete', template })}
                                      title="Delete template"
                                      aria-label={`Delete ${template.name}`}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </section>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* + Add Widget */}
          <div ref={paletteRef} style={{ position: 'relative' }}>
            <Button variant="primary" type="submit" size="sm"
              id="add-widget-btn"
              onClick={(): void => { setPaletteOpen((v) => !v); if (!editMode) setEditMode(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <span>＋</span> Add Widget
            </Button>
            {paletteOpen && (
              <div className="widget-palette-dropdown">
                <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Widget Library</span>
                </div>
                {WIDGET_DEFS.map((def) => (
                  <button key={def.type + '_pal'} onClick={(): void => handleAddWidget(def)} className="widget-palette-item">
                    <span className="wpi-icon">{def.icon}</span>
                    <div className="wpi-body"><b>{def.name}</b><span>{def.description}</span></div>
                    <span className="wpi-add">＋</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Edit Mode toggle */}
          <button
            id="edit-mode-btn"
            onClick={(): void => { setEditMode((v) => !v); setPaletteOpen(false); }}
            style={{
              background: editMode ? 'var(--primary)' : 'var(--primary-soft)',
              color: editMode ? 'white' : 'var(--primary)',
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
              border: `1px solid var(--primary)`, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s',
            }}
          >
            {editMode ? '✓ Done Editing' : '✏️ Edit Layout'}
          </button>
        </div>
      </div>

      {/* Edit mode banner */}
      {editMode && (
        <div className="edit-mode-banner">
          <span>🧱</span>
          <span><b>Edit Mode</b> — Drag widgets by their header to reposition. Drag the resize handle (↘) in the corner to expand or contract. Click <b>×</b> to remove a widget.</span>
        </div>
      )}

      {/* Empty state */}
      {localWidgets.length === 0 && (
        <div style={{ display: 'grid', placeItems: 'center', height: '240px', background: 'white', borderRadius: '12px', border: '2px dashed var(--muted)', margin: '16px 0' }}>
          <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
            <p style={{ marginBottom: '12px' }}>No widgets added to this stage layout yet.</p>
            <button onClick={(): void => setPaletteOpen(true)} style={{ color: 'var(--primary)', fontWeight: 'bold', textDecoration: 'underline' }}>+ Add your first widget</button>
          </div>
        </div>
      )}

      {/* RGL Grid */}
      {localWidgets.length > 0 && (
        <div ref={containerRef} className={`widget-grid-rgl-wrap ${editMode ? 'edit-active' : ''}`}>
          {mounted && (
            <GridLayout
              width={width}
              layout={layout}
              gridConfig={{
                cols: COLS,
                rowHeight: ROW_HEIGHT,
                margin: [gridGap, gridGap] as [number, number],
                containerPadding: [gridGap, gridGap] as [number, number],
                maxRows: MAX_ROWS,
              }}
              dragConfig={{ enabled: editMode, handle: DRAG_HANDLE, cancel: '.wg-no-drag', threshold: 4 }}
              resizeConfig={{ enabled: editMode, handles: ['se', 's', 'e'] }}
              compactor={noCompactor}
              autoSize={true}
              className="widget-grid-rgl"
              onLayoutChange={handleLayoutChange}
              onDragStop={persistFinalLayout}
              onResizeStop={persistFinalLayout}
            >
              {localWidgets.map((widget) => {
                const layoutItem = layout.find((li) => li.i === widget.id);
                if (!layoutItem) return null;
                return (
                  <div key={widget.id} className={`widget-card-rgl${editMode ? ' edit-mode' : ''}`}>
                    <div className={`wg-header${editMode ? ' wg-drag-handle' : ''}`}>
                      <span className="wg-label">{getWidgetLabel(widget.type)}</span>
                      <div className="wg-actions wg-no-drag">
                        <button onClick={(): void => setOpenConfigWidgetId(widget.id)} className="wg-btn-icon" title="Configure Widget">⚙</button>
                        {editMode && (
                          <button onClick={(): void => handleRemoveWidget(widget.id)} className="wg-btn-icon wg-btn-remove" title="Remove Widget">×</button>
                        )}
                      </div>
                    </div>
                    <div className="wg-body">
                      {renderWidgetBody(widget)}
                    </div>
                  </div>
                );
              })}
            </GridLayout>
          )}
        </div>
      )}

      {/* Widget Configuration Modal */}
      {openConfigWidgetId && (
        ((): React.JSX.Element | null => {
          const configWidget = localWidgets.find((w) => w.id === openConfigWidgetId);
          if (!configWidget) return null;
          return (
            <Dialog label="Stage details" onClose={(): void => setOpenConfigWidgetId(null)} pending={false}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {renderWidgetConfigForm(configWidget)}
              </div>
            </Dialog>
          );
        })()
      )}

      {calendarCreateOpen && (
        <CalendarEventEditorModal
          onSave={(evt) => {
            onAddCalendarEvent({
              ...evt,
              id: `evt_${Date.now()}`,
            });
            setCalendarCreateOpen(false);
          }}
          onClose={(): void => setCalendarCreateOpen(false)}
        />
      )}

      <ModalDialog
        isOpen={templateDialog?.kind === 'save'}
        title="Save workspace template"
        message="Save the current widgets, sizes, spacing, positions, and widget settings as your own template."
        type="prompt"
        promptFields={[
          { key: 'name', label: 'Template name', placeholder: 'e.g. Discovery review' },
          { key: 'description', label: 'Description', placeholder: 'When this layout is most useful', required: false },
        ]}
        confirmText="Save template"
        onConfirm={handleSaveTemplate}
        onCancel={(): void => setTemplateDialog(null)}
      />

      <ModalDialog
        isOpen={templateDialog?.kind === 'delete'}
        title="Delete workspace template?"
        message={templateDialog?.kind === 'delete'
          ? `"${templateDialog.template.name}" will be permanently removed. Existing matter layouts will not change.`
          : ''}
        type="confirm"
        confirmText="Delete template"
        onConfirm={(): void => {
          if (templateDialog?.kind === 'delete') void handleDeleteTemplate(templateDialog.template);
        }}
        onCancel={(): void => setTemplateDialog(null)}
      />

      <ModalDialog
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        onConfirm={modalConfig.onConfirm}
        onCancel={(): void => setModalConfig((p) => ({ ...p, isOpen: false }))}
      />
    </div>
  );
}
